import { useState } from "react";
import { Button, Input, Popover, PopoverContent, PopoverTrigger, toast } from "@altimateai/lego";
import { LoadingButton, Stack } from "@altimateai/lego";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { forkKnowledgeSource, forkKnowledgeSourceV2 } from "@/helpers/apis";
import { useTenantInfo } from "@/helpers/hooks";
import { KnowledgeTracking } from "@/lib/tracking/knowledge";

interface ForkKnowledgeBaseTemplateProps {
  templateName: string;
}

export default function ForkKnowledgeBaseTemplate({
  templateName,
}: ForkKnowledgeBaseTemplateProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [newSourceName, setNewSourceName] = useState(templateName);
  const queryClient = useQueryClient();
  const { enableKnowledgeEngine } = useTenantInfo();

  const { mutate, isPending: isForking } = useMutation({
    mutationFn: async (currentNewSourceName: string) => {
      if (enableKnowledgeEngine) {
        await forkKnowledgeSourceV2(templateName, currentNewSourceName);
      } else {
        await forkKnowledgeSource(templateName, currentNewSourceName);
      }
    },
    onSuccess: () => {
      KnowledgeTracking.templateForked(templateName);
      queryClient.invalidateQueries({ queryKey: ["knowledgeSources"] });
      toast({
        description: enableKnowledgeEngine
          ? "Knowledge forked successfully. Processing will begin shortly."
          : "Knowledge Forked Successfully",
        title: "Forked Knowledge",
      });
      setIsOpen(false); // Close popover on success
    },
    onError: (error: Error) => {
      toast({
        description: error.message,
        title: "Error Forking Knowledge",
        variant: "destructive",
      });
    },
  });

  const handleFork = () => {
    if (!newSourceName.trim()) {
      toast({
        description: "Please enter a name for the new source.",
        title: "Name Required",
        variant: "destructive",
      });
      return;
    }
    mutate(newSourceName);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="secondary" onClick={() => setIsOpen(true)}>
          Fork
        </Button>
      </PopoverTrigger>
      <PopoverContent className="al-w-80">
        <Stack direction="column" gap="md">
          <h4 className="al-font-medium al-leading-none">Fork Template</h4>
          <p className="al-text-sm al-text-muted-foreground">
            Enter a name for your new knowledge.
          </p>
          <Input
            value={newSourceName}
            onChange={e => setNewSourceName(e.target.value)}
            placeholder="Enter source name"
          />
          <Stack direction="row" justify="end" gap="sm">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <LoadingButton loading={isForking} onClick={handleFork} size="sm">
              Fork
            </LoadingButton>
          </Stack>
        </Stack>
      </PopoverContent>
    </Popover>
  );
}
